import CMSWrapper from '@/features/shared/CMSWrapper';
import SEOHrefLang from '@/features/shared/SEOHrefLang';
import { ColorNames } from '@/utils/constants';
import { filterObj } from '@/utils/helpers';
import { CTADocumentMapping, MetadataDocumentMapping } from '@/utils/mappings';
import {
  resolveBgColor,
  resolveBool,
  resolveContentBlocksContainer,
  resolveId,
  resolveImage,
  resolveLink,
  resolveReferences,
  resolveRelatedDocument,
  resolveRTC,
  resolveSpacingSize,
  resolveTheme,
  resolveURIList,
} from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import { TaxonomyKey, TaxonomyType, TechPartnerTaxonomy } from '../TechPartner.interface';
import SolutionDetail from './SolutionDetail';

const resolveRelatedDocumentList = ({ rawVal, page, labels = {} }: any) => {
  return rawVal.map((item: any) => resolveRelatedDocument({ rawVal: item, page, labels }));
};

const resolveSummaryBlock = ({ rawVal }: any): TaxonomyType | undefined => {
  if (!rawVal?.length) return undefined;

  let mappedCategories: TechPartnerTaxonomy = {
    industry: { label: 'Industry', values: [] },
    region: { label: 'Availability', values: [] },
    'product-line': { label: 'Product line', values: [] },
  };

  rawVal?.forEach((taxonomy: string) => {
    const splitIndex = taxonomy?.indexOf('/');
    const category = taxonomy?.slice(0, splitIndex);
    const value = taxonomy
      ?.slice(splitIndex + 1)
      ?.split('-')
      ?.filter((word) => !!word?.trim())
      ?.map((word) => word?.[0]?.toUpperCase() + word?.slice(1))
      ?.join(' ')
      ?.trim();

    if (!value) return;
    if (splitIndex && category && category in mappedCategories) {
      const { label, values } = mappedCategories[category as TaxonomyKey];
      mappedCategories = {
        ...mappedCategories,
        [category]: { label, values: [...values, value] },
      };
    }
  });

  const mappedCategoriesFilter: any = filterObj(
    mappedCategories,
    (_: any, category: { values: string | any[] }) => category.values.length > 0,
  );
  const mappedTaxonomy: TaxonomyType = { categories: mappedCategoriesFilter };

  return mappedTaxonomy;
};

const MappedSolutionDetail = ({
  config,
  content,
  contentButtons,
  labels,
  pageModels,
  page,
  tagLinks,
}: any) => {
  const translationConfig = pageModels?.config?.translation;
  const props = useMemo(
    () => ({
      ...content,
      ...config,
      taxonomyList: pageModels?.taxonomy || {},
      labels,
      contentButtons,
      tagLinks,
    }),
    [config, content, contentButtons, labels, pageModels?.taxonomy, tagLinks],
  );

  const { relatedSolutions } = content || {};
  const relatedContentList =
    relatedSolutions && resolveRelatedDocumentList({ rawVal: relatedSolutions, page, labels });

  const cardColor = ColorNames.charcoal;
  const cardTheme = resolveTheme('bg-color')({ rawVal: cardColor });
  const hrefLangProps = React.useMemo(
    () => ({
      hrefLangsRaw: content?.pageMetadata?.hreflangUrl,
      exclusionPaths: translationConfig?.endpoint_exclusion_paths,
      countryCodesWhitelist: translationConfig?.country_codes_whitelist,
    }),
    [
      content?.pageMetadata?.hreflangUrl,
      translationConfig?.endpoint_exclusion_paths,
      translationConfig?.country_codes_whitelist,
    ],
  );

  return (
    <div data-datalayer-position="solution_detail">
      <SolutionDetail
        {...{
          ...props,
          relatedSolutions: relatedContentList,
          cardTheme,
          cardColor,
        }}
      />
      <SEOHrefLang {...hrefLangProps} />
    </div>
  );
};

const SolutionDetailCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        includeBreadcrumbs: ['includeBreadcrumb', resolveBool],
        includeJumplinks: [null, resolveBool],
        displayTags: [null, resolveBool],
        backgroundBannerImage: [null, resolveImage],
        button: [null, resolveLink],
        developedBy: null,
        cta: [
          null,
          {
            ctaContent: [
              'ctaContent',
              {
                document: ['content', CTADocumentMapping],
              },
            ],
            textColor: [null, resolveTheme('text-color')],
            themeBackgroundColor: [null, resolveBgColor],
            topContainerBackgroundColor: [null, resolveBgColor],
            topPadding: [null, resolveSpacingSize],
          },
        ],
        // backgroundBannerVideo: [null, resolveCompound],
        mobileBackgroundBanner: [null, resolveImage],
        logo: [null, resolveImage],
        headline: null,
        shortDescription: [null, resolveRTC],
        pageMetadata: [null, MetadataDocumentMapping],
        cardImage: [null, resolveImage],
        cardDescription: null,
        isGenetecCertified: [null, resolveBool],
        cardButton: [null, resolveLink],
        cardUrl: null,
        detailContent: ['detailContent', resolveContentBlocksContainer],
        relatedSolutionsButton: [null, resolveLink],
        relatedSolutionsTitle: null,
        summary: [
          null,
          { keys: ['taxonomy', resolveSummaryBlock], compatibility: [null, resolveLink] },
        ],
        keys: null,
        contentcategory: 'contentCategory',
        relatedSolutions: [null, resolveReferences],
        cardTileImage: ['cardImage', resolveImage],
        id: [null, resolveId],
      },
    ],
    taxonomy: 'taxonomyDocument',
    'technologyPartnersPage.config': 'config',
    'technologyPartnersPage.labels': 'labels',
    'value-lists/taxonomy-tag-links': ['tagLinks', resolveURIList],
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedSolutionDetail}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default SolutionDetailCMSWrapper;
